import { yupResolver } from '@hookform/resolvers/yup'
import { getQuestionFromElements, useFormFactory } from '@meilleurtaux/form-factory'
import { useDeviceDetection, useTracking } from '@meilleurtaux/form-factory-ds'
import { type FormEvent, useEffect, useMemo } from 'react'
import { type FieldValues, useForm } from 'react-hook-form'
import SlideContent from './SlideContent'
import type { Slide } from '@meilleurtaux/form-factory'
import {
  handleDefaultValues,
  handleYupSchema,
  shouldSkipInlineSave,
  transformResponse,
} from 'utils/questionManager'
import { storage } from 'utils/storage'

const getQuestionsFromSlide = (slide: Slide) =>
  slide.groups.reduce(
    (acc, group) => acc.concat(getQuestionFromElements(group.elements)),
    [] as ReturnType<typeof getQuestionFromElements>,
  )

interface StepContentFormProps {
  currentSlide: Slide
  isLastSlide: boolean
  toggleNextIsLoading: () => void
}

export default function QuestionManagerForm({
  currentSlide,
  isLastSlide,
  toggleNextIsLoading,
}: Readonly<StepContentFormProps>) {
  const { validateSlide, validateForm, setSlideValid, logError } = useFormFactory(
    import.meta.env.VITE_API_URL,
  )
  const device = useDeviceDetection()
  const { trackMultipleQuestionsAnswered } = useTracking()

  const yupSchema = useMemo(() => handleYupSchema(currentSlide), [currentSlide])

  const {
    control,
    getValues,
    handleSubmit,
    getFieldState,
    reset,
    formState: { isValid, errors },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(yupSchema),
    defaultValues: handleDefaultValues(currentSlide),
  })

  useEffect(() => {
    // On décale la mise à jour du state pour résoudre le problème d'animation de Steps
    const timeout = setTimeout(() => setSlideValid(isValid), 0)
    return () => clearTimeout(timeout)
  }, [isValid, setSlideValid])

  // On reset le formulaire à chaque changement sur la slide, en resynchronisant sur la vérité
  // fraîche du moteur (ex: une question masquée/réinitialisée suite à la réponse à une autre
  // question — sinon son champ dirty resterait affiché avec son ancienne valeur alors que le
  // moteur l'a déjà réinitialisée, cf. régression "permis moto -> BSR").
  // Seules les questions dont la sauvegarde inline est volontairement ignorée
  // (shouldSkipInlineSave, ex: checkbox optin cf. MPT-21389) gardent leur valeur dirty : le
  // moteur ne les connaît pas encore tant qu'elles ne sont pas explicitement persistées.
  useEffect(() => {
    const goNext = getQuestionsFromSlide(currentSlide).length === 1
    const preservedValues = getQuestionsFromSlide(currentSlide).reduce<Record<string, unknown>>(
      (acc, question) => {
        if (!shouldSkipInlineSave(question.validatorKind, goNext)) return acc
        const key =
          question.validatorKind === 'Date' ? `date_${question.extraInfo}` : question.extraInfo
        if (getFieldState(key).isDirty) acc[key] = getValues(key)
        return acc
      },
      {},
    )

    reset(
      { ...handleDefaultValues(currentSlide), ...preservedValues },
      {
        keepErrors: true,
        keepDirty: true,
        keepTouched: true,
      },
    )
  }, [currentSlide, reset, getFieldState, getValues])

  const onSubmitSuccess = (data: FieldValues) => {
    // On construit l'objet à envoyé pour validation coté serveur
    let responses: { key: string; response: string; responseLabel?: string }[] = Object.keys(
      data,
    ).map((key) => ({
      key: key,
      response: data[key] as string,
      responseLabel: undefined,
    }))

    // On transforme certaines réponses (ex: date)
    responses = transformResponse(responses, currentSlide).filter(
      (response) => response.response !== '',
    )

    // Si c'est la dernière étape on valide le formulaire
    if (isLastSlide) validateForm(responses)
    // Sinon on valide l'étape
    else {
      validateSlide(responses)
    }

    // Tracking : on envoie les questions répondues avec leur libellé et leur ordre dans la slide
    const slideQuestions = getQuestionFromElements(
      currentSlide.groups.flatMap((group) => group.elements),
    )
    const responsesForTracking = responses.map((response) => {
      const question = slideQuestions.find((q) => q.extraInfo === response.key)
      return {
        questionExtraInfo: response.key,
        questionTitle: question?.label ?? '',
        questionOrder: question?.order ?? 0,
      }
    })

    trackMultipleQuestionsAnswered(responsesForTracking)
  }

  const onSubmitError = (errors: object) => {
    // Récupéré les extraInfo et les valeurs des erreurs
    const errorsExtraInfoValue: { key: string; value: string }[] = Object.keys(errors).map(
      (key) => ({
        key,
        value: getValues(key) as string,
      }),
    )

    for (const { key, value } of errorsExtraInfoValue) {
      logError({
        userSheetId: storage.getValue('FF_UserSheetId') ?? '',
        extraInfo: key,
        userAnswer: value,
        tunnelId: Number.parseInt(storage.getValue('FF_TunnelId') ?? '0'),
        variationId: Number.parseInt(storage.getValue('FF_VariationId') ?? '0'),
      })
    }
  }

  const goNextOverride = (ev: FormEvent) => {
    toggleNextIsLoading()
    ev.preventDefault()
    handleSubmit(onSubmitSuccess, onSubmitError)()
    toggleNextIsLoading()
  }

  const goNextOnSelect = useMemo(
    () => getQuestionsFromSlide(currentSlide).length === 1,
    [currentSlide],
  )

  return (
    <form
      onSubmit={goNextOverride}
      id="stepForm"
      tabIndex={-1}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        gap: device === 'Mobile' ? '20px' : '32px',
      }}
    >
      <SlideContent
        slide={currentSlide}
        control={control}
        getFieldState={getFieldState}
        errors={errors}
        validationSchema={yupSchema}
        goNextOnSelect={goNextOnSelect}
      />
    </form>
  )
}
